import Component from '../abstracts/Component';
import Cookies from 'js-cookie';
import NewsletterLogic from '../logics/NewsletterLogic';
import {IS_PRIVACY_PAGE} from '../utils/Environment';
import { trapFocusOnModal, removeTrapFocusOnModal, getFocusableElements } from '../utils/TrapFocus';

export default class NewsletterPopupComponent extends Component {

  get SELECTORS() {
    return {
      newsletterFormWrapper: '.news__form__wrapper',
      form: '.w-newsletter-popup .js-newsletter-form',
      jsContent: '.js-modal-form-contents ',
      submitButton: '#newsletterForm-modal > .js-nl-submission',
      submitButtonMobile: '#newsletterForm-modal > .js-nl-submission.mobile',
      privacyCheckbox: '#privacy-news',
      close: '[data-close-popup]',
      newsletterPopupForm: '#newsletterPopupForm',
      inputEmail: 'input[type=email]'
    };
  }

  constructor(compEl) {
    super(compEl);
    this.closeBtn = this.$component.querySelector(this.SELECTORS.close);
    this.form = this.$component.querySelector(this.SELECTORS.newsletterPopupForm);
    this.jsContent = this.$component.querySelector(this.SELECTORS.jsContent);
    this.privacyCheckbox = this.$component.querySelector(this.SELECTORS.privacyCheckbox);
    this.submitButton = this.$component.querySelector(this.SELECTORS.submitButton);
    this.submitButtonMobile = this.$component.querySelector(this.SELECTORS.submitButtonMobile);
    
    // Focus management properties
    this.trapFocusHandler = null;
    this.lastActiveElement = null;
    this.isPopupAccessibilityActive = false;
    this.handleEscKey = null;
  }

  // istantiate properties
  initProperties() {
    let contextPath = document.getElementsByTagName('HTML')[0].getAttribute("data-context");
    this.modalkey = $(document.body).data('nlKey') ? $(document.body).data('nlKey') : 'default_modal';
    this.hideCookieKey = 'nl_' + this.modalkey + '_hide';
    this.link = $(document.body).data('nlLink') ? $(document.body).data('nlLink') : contextPath+'/modals/newsletter';
  }

  // enable form submit button
  enableSubmitBtn(submitButton, privacyCheckbox) {
    if (privacyCheckbox.checked === true) {
      submitButton.disabled = false;
    } else {
      submitButton.disabled = true;
    }
    
    // Update focus trap when button state changes
    this.updateFocusTrap();
  }

  // init modal form function
  initModalForm() {
    return $.ajax({
      url: `${this.link}?xhr=true`,
      dataType: 'html'
    });
  }

  // Check if Personal Stylist popup is open and transfer focus to it
  checkAndActivatePersonalStylistFocus() {
    const personalStylistPopup = document.querySelector('.live-chat-personal-stylist-cta__wrapper');
    
    if (!personalStylistPopup || 
        personalStylistPopup.classList.contains('d-none') ||
        personalStylistPopup.getAttribute('aria-hidden') !== 'false') {
      return; // Early return if popup is not visible or active
    }
    
    // Dispatch event to notify Personal Stylist component
    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('newsletter-popup-closed', {
        detail: { shouldActivatePersonalStylistFocus: true }
      }));
    });
  }

  // ajax form in the popup
  insertPopupForm() {
    this.initModalForm().then(response => {
      let html = new DOMParser().parseFromString(response, 'text/html');
      this.log('insert form');
      // inject html
      this.jsContent.innerHTML = html.querySelector('.js-modal-form-contents').innerHTML;
      this.$emit(this.$customEvents.NL_POPUP_EVENTS.show);
    });
  }

  // Get focusable elements in the newsletter popup
  getFocusableElementsInPopup() {
    return getFocusableElements(this.$component);
  }

  // Update focus trap when form elements change state
  updateFocusTrap() {
    if (this.isPopupAccessibilityActive && this.trapFocusHandler) {
      // Remove old trap
      removeTrapFocusOnModal(this.$component, this.trapFocusHandler);
      // Create new trap with updated elements
      this.trapFocusHandler = trapFocusOnModal(this.$component);
    }
  }

  // Set focus to the first available element in the popup
  setInitialFocus() {
    const focusableElements = this.getFocusableElementsInPopup();
    const targetElement = focusableElements.length > 0 ? focusableElements[0] : this.closeBtn;
    
    if (targetElement) {
      targetElement.focus();
    }
  }

  // Activate popup accessibility features
  activatePopupAccessibility() {
    if (!this.$component || this.isPopupAccessibilityActive) return;
    
    this.lastActiveElement = document.activeElement;
    this.$component.setAttribute('aria-hidden', 'false');
    this.trapFocusHandler = trapFocusOnModal(this.$component);
    
    // ESC key listener
    this.handleEscKey = e => {
      if (e.key === 'Escape' && this.$component.classList.contains('show')) {
        this.closeBtn.click();
      }
    };
    document.addEventListener('keydown', this.handleEscKey);
    
    // Set initial focus
    requestAnimationFrame(() => this.setInitialFocus());
    
    this.isPopupAccessibilityActive = true;
  }

  // Deactivate popup accessibility features
  deactivatePopupAccessibility() {
    if (!this.$component || !this.isPopupAccessibilityActive) return;
    
    if (this.trapFocusHandler) {
      removeTrapFocusOnModal(this.$component, this.trapFocusHandler);
      this.trapFocusHandler = null;
    }
    
    if (this.handleEscKey) {
      document.removeEventListener('keydown', this.handleEscKey);
      this.handleEscKey = null;
    }
    
    this.$component.setAttribute('aria-hidden', 'true');
    
    if (this.lastActiveElement) {
      this.lastActiveElement.focus();
      this.lastActiveElement = null;
    }
    
    this.isPopupAccessibilityActive = false;
  }

  bindEvents() {
    // show events
    // if comm popup is hidden
    this.$on(this.$customEvents.COM_POPUP_EVENTS.hidden, () => {
      // check the cookies
      if (!Cookies.get(this.hideCookieKey) && !IS_PRIVACY_PAGE) {
        // insert form
        this.insertPopupForm();
      }
    });
    // show popup
    this.$on(this.$customEvents.NL_POPUP_EVENTS.show, () => {
      this.$component.classList.add('show');
      this.closeBtn.addEventListener('click', () => {
        this.$emit(this.$customEvents.NL_POPUP_EVENTS.hide);
      });
      
      // Activate accessibility features including focus management
      this.activatePopupAccessibility();
    });
    // hide popup
    this.$on(this.$customEvents.NL_POPUP_EVENTS.hide, () => {
      this.$component.classList.remove('show');
      
      // Deactivate accessibility features
      this.deactivatePopupAccessibility();
      
      // Check if Personal Stylist popup is open and needs focus
      this.checkAndActivatePersonalStylistFocus();
      
      this.$emit(this.$customEvents.NL_POPUP_EVENTS.hidden);
    });
    // on hide popup i set the cookie
    this.$on(this.$customEvents.NL_POPUP_EVENTS.hidden, () => {
      Cookies.set(this.hideCookieKey, 'true', {
        expires: 365,
        path: '/'
      });
    });
    // on subscribed popup i set the cookie
    this.$on(this.$customEvents.NL_POPUP_EVENTS.subscribed, () => {
      Cookies.set(this.hideCookieKey, 'true', {
        expires: 365,
        path: '/'
      });
      
      // Move focus to close button after successful subscription
      if (this.closeBtn && this.isPopupAccessibilityActive) {
        this.closeBtn.focus();
      }
    });

    // privacy button - popup
    this.$on(this.$customEvents.NL_POPUP_EVENTS.show, () => {
      // enable submit on show form
      // bind click, re-declare variables
      const privacyCheckbox = this.$component.querySelector(this.SELECTORS.privacyCheckbox);
      const submitButton = this.$component.querySelector(this.SELECTORS.submitButton);
      const submitButtonMobile = this.$component.querySelector(this.SELECTORS.submitButtonMobile);

      privacyCheckbox.addEventListener('click', e => {
        this.enableSubmitBtn(submitButton, privacyCheckbox);
        this.enableSubmitBtn(submitButtonMobile, privacyCheckbox);
      });
    });

    //
    this.$on('submit.newsletter', this.NewsletterLogic.subscribeToNewsletter, 'body', this.SELECTORS.form, this);
  }

  render() {
    this.log('Rendering...');
    // loading logics
    this.NewsletterLogic = new NewsletterLogic();
    // properties
    this.initProperties();
    // events
    this.bindEvents();
    // choose to open or wait comm popup to close
    let communicationCookie = Cookies.get('communications-cookie');
    // if there is the cookie, i fire the newsletter popup
    if (communicationCookie || document.querySelector('.w-communication-popup') == null) {
      this.$emit(this.$customEvents.COM_POPUP_EVENTS.hidden);
    }
  }
}
